import { Builder } from '@builder.io/react';
import { BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES } from '../BuilderComponentsName';
import { BuilderS1Hero } from '../components/interactive-sections/S1Hero';
import { backgroundGradientOptions, textColorOptionsLimited, buttonVariantOptions } from '../utils/builderColorOptions';
import { getBackgroundColorInput, getBackgroundGradientInput, getPaddingInputs } from '../utils/builderSharedInputs';

const iconOptions = [
    { label: 'Google', value: 'FaGoogle' },
    { label: 'Yelp', value: 'FaYelp' },
];

Builder.registerComponent(BuilderS1Hero, {
    name: BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES.S1Hero,
    friendlyName: 'S1 Hero',
    inputs: [
        {
            name: 'heroTitle',
            type: 'string',
            required: true,
            defaultValue: 'Underarm Laser Hair Removal',
            friendlyName: 'Headline',
            helperText: 'Main headline text for the hero section. This field is overriden on state pages to display the state name. Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}',
        },
        {
            name: 'heroTag',
            type: 'string',
            defaultValue: 'h1',
            enum: ['h1', 'h2'],
            friendlyName: 'Headline Tag',
            helperText: 'Choose whether the headline should be an H1 or H2 tag for SEO purposes',
        },
        {
            name: 'heroTitleColor',
            type: 'string',
            defaultValue: 'Turquoise',
            enum: textColorOptionsLimited,
            friendlyName: 'Headline Color',
            helperText: 'Color for the headline text',
        },
        {
            name: 'heroText',
            type: 'string',
            required: true,
            defaultValue:
                'Raise your arms and wear sleeveless tops with confidence and smooth underarms! With underarm laser hair removal treatments, you can say goodbye to coarse hair, itchy pits, and prickly unwanted hair. Throw away the razor, toss out the at-home laser hair removal devices, and get smooth pits for good!',
            friendlyName: 'Hero Text',
            helperText: 'Supporting text content below the headline. Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}',
        },
        {
            name: 'heroTextColor',
            type: 'string',
            defaultValue: 'Navy',
            enum: textColorOptionsLimited,
            friendlyName: 'Hero Text Color',
            helperText: 'Color for the main text content',
        },
        {
            name: 'imageURL',
            type: 'file',
            defaultValue:
                'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F05469c89ae97422fbd33b56b1a604050',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'gif', 'svg'],
            friendlyName: 'Image',
            helperText: 'Upload an image or provide an image URL for the hero section',
        },
        {
            name: 'imageAltText',
            type: 'string',
            friendlyName: 'Image Alt Text',
            required: true,
            defaultValue: 'Underarm laser hair removal treatment',
            helperText: 'Alt text for the image',
        },
        {
            name: 'imageFlip',
            type: 'boolean',
            defaultValue: false,
            friendlyName: 'Image Display Side',
            helperText: 'Toggle image position from right to left',
        },
        {
            name: 'buttons',
            type: 'list',
            friendlyName: 'Buttons',
            helperText: 'Add one or more call-to-action buttons that will appear below the hero text (maximum 2 buttons allowed)',
            onChange: (options) => {
                const items = options.get('buttons');
                if (items.length > 2) {
                    options.set('buttons', items.slice(0, 2));
                    alert('Maximum 2 buttons allowed');
                }
            },
            defaultValue: [
                {
                    text: 'Learn More',
                    variant: 'primary',
                    url: '/path',
                    icon: '',
                },
            ],
            subFields: [
                {
                    name: 'text',
                    type: 'string',
                    required: true,
                    friendlyName: 'Button Text',
                    defaultValue: 'Button Text',
                    helperText: 'The text that will appear on the button (keep it short and action-oriented)',
                },
                {
                    name: 'variant',
                    type: 'string',
                    required: true,
                    friendlyName: 'Button Variant',
                    enum: buttonVariantOptions,
                    defaultValue: 'primary',
                    helperText:
                        'The style of the button: primary (blue), secondary (navy blue - darker than primary), cta (special emphasis)',
                },
                {
                    name: 'url',
                    type: 'string',
                    required: true,
                    friendlyName: 'Button URL',
                    defaultValue: '/path',
                    helperText:
                        'The destination page when the button is clicked (e.g., /services, /contact, /special-offer)',
                },
                {
                    name: 'icon',
                    type: 'string',
                    required: false,
                    friendlyName: 'Button Icon',
                    enum: iconOptions.map(option => option.label),
                    defaultValue: '',
                    helperText: 'Optional icon to display in the button (Google or Yelp)',
                }
            ],
        },
        ...getBackgroundColorInput({}),
        ...getBackgroundGradientInput({
            backgroundGradientName: 'gradient',
            defaultValue: 'BlueTop',
            backgroundGradientList: backgroundGradientOptions,
        }),
        {
            name: 'backgroundImage',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'gif', 'svg'],
            friendlyName: 'Background Image',
            helperText: 'Upload a background image or provide an image URL (optional)',
        },
        ...getPaddingInputs({
            desktopTop: 'small',
            desktopBottom: 'small',
            mobileTop: 'small',
            mobileBottom: 'small',
        }),
    ],
});
